import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { useAdminApi } from '@/hooks/useAdminApi';
import {
  BarChart3,
  Building2,
  Calendar,
  Dumbbell,
  Loader2,
  TrendingUp,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

interface DashboardData {
  overview: {
    total_affiliates: number;
    total_agents: number;
    total_members: number;
    active_subscriptions: number;
  };
  growth: {
    new_affiliates_this_month: number;
    pending_approvals: number;
  };
  commissions: {
    pending_amount: number;
    paid_amount: number;
    pending_payouts: number;
  };
}

export default function AdminAnalytics() {
  const adminApi = useAdminApi();

  // Data state
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [recentScores, setRecentScores] = useState<
    Array<{
      id: string;
      user: { name: string | null };
      workout: { name: string | null };
      value: number;
      created_at: string;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');

  // Fetch analytics data
  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [dashboardRes, scoresRes] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getScores({ limit: 10 }),
      ]);

      if (dashboardRes.success && dashboardRes.data) {
        setDashboardData(dashboardRes.data);
      }

      if (scoresRes.success && scoresRes.data) {
        setRecentScores(scoresRes.data.scores);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  }, [adminApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Format currency
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(cents / 100);
  };

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All time' },
  ];

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Analytics</h1>
          <p className="mt-1 text-muted-foreground">
            Platform activity, metrics, and recent actions.
          </p>
        </div>
        <Select
          options={dateRangeOptions}
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        />
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <Link to="/admin/affiliates">
          <Card className="animate-fade-in cursor-pointer transition-colors hover:border-primary/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-primary/10 p-3">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Affiliates</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? '...' : dashboardData?.overview.total_affiliates || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/users?role=member">
          <Card
            className="animate-fade-in cursor-pointer transition-colors hover:border-blue-500/50"
            style={{ animationDelay: '50ms' }}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-blue-500/10 p-3">
                  <Users className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Members</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? '...' : dashboardData?.overview.total_members || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/subscriptions">
          <Card
            className="animate-fade-in cursor-pointer transition-colors hover:border-green-500/50"
            style={{ animationDelay: '100ms' }}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-green-500/10 p-3">
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? '...' : dashboardData?.overview.active_subscriptions || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/users?role=member">
          <Card
            className="animate-fade-in cursor-pointer transition-colors hover:border-orange-500/50"
            style={{ animationDelay: '150ms' }}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-orange-500/10 p-3">
                  <UserPlus className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">New This Month</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? '...' : dashboardData?.growth.new_affiliates_this_month || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Commission Overview */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Commission Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Commission Overview
            </CardTitle>
            <CardDescription>Agent commission metrics</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Payouts</p>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(dashboardData?.commissions.pending_amount || 0)}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-500">
                    {dashboardData?.commissions.pending_payouts || 0} pending
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Paid</p>
                    <p className="text-2xl font-bold text-green-500">
                      {formatCurrency(dashboardData?.commissions.paid_amount || 0)}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-green-500/10 text-green-500">
                    Paid
                  </Badge>
                </div>
                <div className="pt-4 border-t">
                  <Button variant="outline" className="w-full" asChild>
                    <a href="/admin/commissions">View Full Report</a>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Platform Stats */}
      <div className="grid gap-6 lg:grid-cols-3 mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Dumbbell className="h-5 w-5 text-primary" />
              Workout Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Scores Logged Today</span>
                <span className="font-medium">
                  {
                    recentScores.filter((s) => {
                      const scoreDate = new Date(s.created_at);
                      const today = new Date();
                      return scoreDate.toDateString() === today.toDateString();
                    }).length
                  }
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Scores This Week</span>
                <span className="font-medium">{recentScores.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Athletes</span>
                <span className="font-medium">
                  {new Set(recentScores.map((s) => s.user.name)).size}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Approvals Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pending Affiliates</span>
                <Badge variant="outline" className="bg-orange-500/10 text-orange-500">
                  {dashboardData?.growth.pending_approvals || 0}
                </Badge>
              </div>
              <div className="pt-4">
                <Button variant="outline" className="w-full" asChild>
                  <a href="/admin/affiliates?status=pending">Review Approvals</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Agent Network
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Agents</span>
                <span className="font-medium">{dashboardData?.overview.total_agents || 0}</span>
              </div>
              <div className="pt-4">
                <Button variant="outline" className="w-full" asChild>
                  <a href="/admin/users?role=agent">View Agents</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
